import React, { useEffect, useMemo } from 'react';

import InputText from '../../../components/agv-forms-input';
import { Banner, Card, FileDropZone, SectionLabel } from '../clients-ui';
import { LOGO_ACCEPTED_EXTENSIONS, LOGO_MAX_SIZE_MB } from '../client-defaults';

export const isSiretValid = (siret) =>
  !siret || /^\d{14}$/.test(siret.replace(/\s/g, ''));

const isCoordinateValid = (value, limit) =>
  value === '' ||
  (!Number.isNaN(Number(value)) && Math.abs(Number(value)) <= limit);

export const isFactoryValid = (factory) =>
  !factory ||
  (factory.name.trim().length > 0 &&
    isCoordinateValid(factory.latitude, 90) &&
    isCoordinateValid(factory.longitude, 180));

export const isInformationStepValid = (information, factory) =>
  information.name.trim().length > 0 &&
  isSiretValid(information.siret) &&
  isFactoryValid(factory);

const Toggle = ({ checked, onChange, label, description }) => (
  <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="tw-mt-0.5 tw-size-4 tw-cursor-pointer tw-accent-main"
    />
    <span className="tw-flex tw-flex-col">
      <span className="tw-text-[13px] tw-text-black">{label}</span>
      <span className="tw-text-[11px] tw-text-[#76808c]">{description}</span>
    </span>
  </label>
);

export default function InformationStep({
  information,
  setInformation,
  factory,
  setFactory,
  existingFactories,
  logoUrl,
  logoFile,
  setLogoFile,
  isLogoUploading,
}) {
  const update = (key) => (value) =>
    setInformation((previous) => ({ ...previous, [key]: value }));
  const updateFactory = (key) => (value) =>
    setFactory((previous) => ({ ...previous, [key]: value }));

  const logoPreviewUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile]
  );

  useEffect(
    () => () => logoPreviewUrl && URL.revokeObjectURL(logoPreviewUrl),
    [logoPreviewUrl]
  );

  const displayedLogo = logoPreviewUrl || logoUrl;
  const isEdition = existingFactories != null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card
        title="Informations du client"
        subtitle="Raison sociale et coordonnées, reprises sur les factures et les documents générés."
      >
        <InputText
          value={information.name}
          setValue={update('name')}
          fieldTitle="Nom du client"
          isRequired
          emptyError={'Le champ "Nom du client" ne peut être vide.'}
          placeholder="Coopérative du Val"
        />
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={information.siret}
            setValue={update('siret')}
            fieldTitle="SIRET"
            placeholder="123 456 789 00012"
            isBadFormat={!isSiretValid(information.siret)}
          />
          <InputText
            value={information.tva}
            setValue={update('tva')}
            fieldTitle="N° de TVA intracommunautaire"
            placeholder="FR12345678901"
          />
        </div>
        <InputText
          value={information.street}
          setValue={update('street')}
          fieldTitle="Adresse"
          placeholder="12 rue des Moulins"
        />
        <div className="tw-grid tw-grid-cols-3 tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={information.zipcode}
            setValue={update('zipcode')}
            fieldTitle="Code postal"
            placeholder="51100"
          />
          <InputText
            value={information.city}
            setValue={update('city')}
            fieldTitle="Ville"
            placeholder="Reims"
          />
          <InputText
            value={information.country}
            setValue={update('country')}
            fieldTitle="Pays"
            placeholder="France"
          />
        </div>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <SectionLabel>Communications</SectionLabel>
          <Toggle
            checked={information.is_email_sending_enabled}
            onChange={update('is_email_sending_enabled')}
            label="Envoi d’emails activé"
            description="Notifications aux destinataires et invitations des utilisateurs."
          />
          <Toggle
            checked={information.is_sms_sending_enabled}
            onChange={update('is_sms_sending_enabled')}
            label="Envoi de SMS activé"
            description="SMS facturés au client selon la tarification."
          />
        </div>
      </Card>

      <Card
        title="Logo"
        subtitle="Affiché dans le backoffice et sur les documents. Stocké sur le bucket public agrivitech-client-brand-logo."
      >
        <div className="tw-flex tw-items-stretch tw-gap-4 mq750:tw-flex-col">
          <div className="tw-flex tw-h-[120px] tw-w-[200px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-[rgba(118,128,140,0.04)] mq750:tw-w-full">
            {displayedLogo ? (
              <img
                src={displayedLogo}
                alt="Logo du client"
                className="tw-max-h-[96px] tw-max-w-[176px] tw-object-contain"
              />
            ) : (
              <span className="tw-text-[11px] tw-text-[#76808c]">
                Aucun logo
              </span>
            )}
          </div>
          <div className="tw-flex-1">
            <FileDropZone
              accept={LOGO_ACCEPTED_EXTENSIONS}
              maxSizeMb={LOGO_MAX_SIZE_MB}
              onFile={setLogoFile}
              disabled={isLogoUploading}
            />
          </div>
        </div>
        {logoFile && !isEdition && (
          <p className="tw-m-0 tw-text-[11px] tw-text-[#76808c]">
            {logoFile.name} sera envoyé à la création du client.
          </p>
        )}
      </Card>

      <Card
        title="Usine / dépôt de départ"
        subtitle="Point de départ des tournées. Indispensable si l’option « Usine de rattachement obligatoire » est activée."
      >
        {isEdition ? (
          existingFactories.length > 0 ? (
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
              {existingFactories.map((existingFactory) => (
                <li
                  key={existingFactory.id}
                  className="tw-text-[13px] tw-text-black"
                >
                  <span className="tw-font-medium">{existingFactory.name}</span>
                  <span className="tw-text-[#76808c]">
                    {' · '}
                    {[
                      existingFactory.street,
                      existingFactory.zipcode,
                      existingFactory.city,
                    ]
                      .filter(Boolean)
                      .join(' ') || 'adresse non renseignée'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Banner tone="warn">
              Ce client n’a aucune usine. Les usines se gèrent ensuite depuis
              les écrans du client.
            </Banner>
          )
        ) : (
          <>
            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
              <input
                type="checkbox"
                checked={factory != null}
                onChange={(event) =>
                  setFactory(
                    event.target.checked
                      ? {
                          name: information.name
                            ? `Dépôt ${information.name}`
                            : '',
                          street: information.street,
                          zipcode: information.zipcode,
                          city: information.city,
                          latitude: '',
                          longitude: '',
                        }
                      : null
                  )
                }
                className="tw-size-4 tw-cursor-pointer tw-accent-main"
              />
              <span className="tw-text-[13px] tw-text-black">
                Créer une première usine avec le client
              </span>
            </label>
            {factory && (
              <div className="tw-flex tw-flex-col tw-gap-4">
                <InputText
                  value={factory.name}
                  setValue={updateFactory('name')}
                  fieldTitle="Nom de l’usine"
                  isRequired
                  emptyError={'Le champ "Nom de l’usine" ne peut être vide.'}
                />
                <InputText
                  value={factory.street}
                  setValue={updateFactory('street')}
                  fieldTitle="Adresse"
                />
                <div className="tw-grid tw-grid-cols-4 tw-gap-4 mq750:tw-grid-cols-1">
                  <InputText
                    value={factory.zipcode}
                    setValue={updateFactory('zipcode')}
                    fieldTitle="Code postal"
                  />
                  <InputText
                    value={factory.city}
                    setValue={updateFactory('city')}
                    fieldTitle="Ville"
                  />
                  <InputText
                    value={factory.latitude}
                    setValue={updateFactory('latitude')}
                    fieldTitle="Latitude"
                    placeholder="49.258"
                    isBadFormat={!isCoordinateValid(factory.latitude, 90)}
                  />
                  <InputText
                    value={factory.longitude}
                    setValue={updateFactory('longitude')}
                    fieldTitle="Longitude"
                    placeholder="4.031"
                    isBadFormat={!isCoordinateValid(factory.longitude, 180)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
